import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LockIcon from "@mui/icons-material/Lock";
import Collapse from "@mui/material/Collapse";
import type { ReactNode } from "react";

interface AccordionProps {
  title: string;
  description: string;
  icon: ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  children: ReactNode;
  locked?: boolean;
  onUpgradeClick?: () => void;
}

export default function Accordion({
  title,
  description,
  icon,
  isOpen,
  onToggle,
  children,
  locked = false,
  onUpgradeClick,
}: AccordionProps) {
  return (
    <div
      className="rounded-card bg-white mb-8 transition-shadow duration-200"
      style={{
        border: "1px solid var(--color-smoke)",
        boxShadow: isOpen ? "0 4px 20px rgba(51,51,51,0.06)" : "none",
      }}
    >
      {/* Header — split into toggle button + optional upgrade button to avoid nested <button> */}
      <div className="flex items-center w-full" style={{ padding: "20px", gap: "12px" }}>
        {/* Toggle area — icon, text, chevron */}
        <button
          type="button"
          className="flex items-center gap-6 flex-1 min-w-0 text-left"
          onClick={onToggle}
          aria-expanded={isOpen}
        >
          {/* Icon — 44×44 light-dust container */}
          <div
            className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: "var(--color-light-dust)", padding: "4px" }}
          >
            {icon}
          </div>

          {/* Title + description */}
          <div className="flex flex-col gap-3 flex-1 min-w-0">
            <p className="font-crimson text-[18px] font-normal leading-none text-rodeo-black">
              {title}
            </p>
            <p className="font-lato text-[12px] text-graphite leading-none">{description}</p>
          </div>

          <KeyboardArrowDownIcon
            sx={{
              fontSize: 32,
              color: "var(--color-graphite)",
              flexShrink: 0,
              transition: "transform 0.25s ease",
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            }}
          />
        </button>

        {/* Upgrade badge — sibling to toggle button, never nested inside it */}
        {locked && (
          <button
            type="button"
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-pill font-lato text-[9px] font-bold uppercase tracking-[1.2px] whitespace-nowrap shrink-0 transition-colors"
            style={{
              background: "var(--color-light-dust)",
              border: "1px solid var(--color-smoke)",
              color: "var(--color-dark-dust)",
            }}
            onClick={() => onUpgradeClick?.()}
          >
            <LockIcon sx={{ fontSize: 11 }} />
            Upgrade to Edit
          </button>
        )}
      </div>

      {/* Body — MUI Collapse handles the height animation */}
      <Collapse in={isOpen} unmountOnExit>
        <div className="h-px mx-5" style={{ background: "var(--color-smoke)" }} />
        <div
          className="px-5 pb-6 pt-px"
          style={locked ? { opacity: 0.55, pointerEvents: "none", userSelect: "none" } : undefined}
        >
          {children}
        </div>
      </Collapse>
    </div>
  );
}
